import type { UserDetails } from '@assembly-web/services';
import type {
  CriteriaItemProps,
  PeopleWithAccessProps,
  PreviewMembersProps,
} from '@assembly-web/ui';
import { Button, ShareConfirmationModal, ShareModal } from '@assembly-web/ui';
import { useEffect, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useImmer } from 'use-immer';

import { useToastStore } from '../../../../stores/useToastStore';
import { usePreviewShareCollectionRules } from '../../hooks/shareCollections/usePreviewShareCollectionRules';
import { useShareCollectionsMemberSearch } from '../../hooks/shareCollections/useShareCollectionsMemberSearch';
import { useShareCollectionsCriteriaRules } from '../../hooks/shareCollections/useShareCollectionsModal';
import { trackSharesheetAction } from '../../services/analytics';

export const ShareCollectionsMessageKey = 'OPEN_SHARE_COLLECTIONS_MODAL';

type ShareCollectionsMessageType = typeof ShareCollectionsMessageKey;

type ShareCollectionsMessagePayload = {
  collectionId: string;
  createdBy?: string;
};

const messages = defineMessages({
  confirmationModalTitle: {
    defaultMessage: 'Done Editing?',
    id: '1PH1ei',
  },
  confirmationModalBody: {
    defaultMessage:
      'You have unsaved changes. Before you go, choose whether you’d like to save or discard them.',
    id: 'jhcPUj',
  },
  discardChanges: {
    defaultMessage: 'Discard Changes',
    id: 'sLnDOb',
  },
  saveChanges: {
    defaultMessage: 'Save Changes',
    id: '3VI9mt',
  },
  successMessage: {
    defaultMessage: 'Updated permissions successfully',
    id: 'wZJxLk',
  },
});

export function ShareCollectionModal({
  currentUserDetails,
}: {
  currentUserDetails: UserDetails;
}) {
  const [collectionId, setCollectionId] = useState<string | null>(null);
  const [collectionOwnerId, setCollectionOwnerId] = useState<string | null>(
    null
  );
  const [rules, setRules] = useImmer<CriteriaItemProps[]>([]);
  const [isPinned, onIsPinnedChange] = useState(false);
  const [view, setView] = useState<'peopleWithAccess' | 'previewMembers'>(
    'peopleWithAccess'
  );
  const [isConfirmationModalOpen, setIsConfirmationModalOpen] =
    useState<boolean>(false);
  const [hasRulesChanged, setHasRulesChanged] = useState<boolean>(false);

  const { formatMessage } = useIntl();
  const { showSuccessToast } = useToastStore();

  const closeShareCollectionModal = () => {
    setCollectionId(null);
    setRules([]);
    onCustomRuleSelect(null);
    setView('peopleWithAccess');
  };

  const handleCloseWithConfirmationModal = () => {
    if (hasRulesChanged) {
      setIsConfirmationModalOpen(true);
    } else {
      closeShareCollectionModal();
    }
  };

  const {
    onRulesSelectionChange,
    onRulesPermissionChange,
    onCustomRulesAction,
    customRuleOptions,
    onCustomRuleSelect,
    selectedCustomRule,
    customRules,
    isLoading,
    onDoneAction,
    handleOnEmailInvite,
    isSaveInProgress,
  } = useShareCollectionsCriteriaRules({
    rules,
    setRules,
    enabled: view === 'peopleWithAccess' && Boolean(collectionId),
    collectionId,
    isPinned,
    onIsPinnedChange,
    collectionOwnerId,
    onSaveCriteriaSuccess: () => {
      closeShareCollectionModal();
      showSuccessToast(formatMessage(messages.successMessage));
    },
    setHasRulesChanged,
  });

  const {
    isLoadingMembers,
    hasNextPage,
    isFetchingNextPage,
    searchTerm,
    members,
    onMemberSearch,
    onLoadMore,
    canInviteCurrentUser,
    hasUserInvitePermission,
  } = useShareCollectionsMemberSearch({
    collectionId,
    enabled: view === 'peopleWithAccess',
  });

  const {
    displayRules,
    previewFilterOptions,
    onSearchOrFilterList,
    isLoading: isLoadingPreview,
    isFetchingNextPage: isFetchingNextPageForPreview,
    hasNextPage: hasNextPageForPreview,
    fetchNextPage: fetchNextPageForPreview,
  } = usePreviewShareCollectionRules({
    rules,
    enabled: view === 'previewMembers',
    collectionOwnerId,
    currentUserDetails,
  });

  const handleMessage = ({
    data: { type, payload },
  }: MessageEvent<{
    type: ShareCollectionsMessageType;
    payload: ShareCollectionsMessagePayload;
  }>) => {
    switch (type) {
      case ShareCollectionsMessageKey:
        setCollectionId(payload.collectionId);
        setCollectionOwnerId(payload.createdBy ?? null);
        break;
    }
  };

  useEffect(() => {
    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const props: PeopleWithAccessProps | PreviewMembersProps =
    view === 'peopleWithAccess'
      ? {
          view: 'peopleWithAccess',
          searchTerm: searchTerm,
          rules: rules,
          isLoading,
          isLoadingMembers,
          isFetchingMoreMembers: isFetchingNextPage,
          hasNextPageForMembers: hasNextPage ?? false,
          hasNextPageForRules: false,
          members: canInviteCurrentUser ? [] : members,
          onLoadMore,
          onRulesSelectionChange,
          onRulesPermissionChange,
          onCustomRulesAction,
          customRuleOptions,
          customRules,
          onMemberSearch,
          onPreviewButtonClick: () => {
            setView('previewMembers');
            trackSharesheetAction({
              actionName: 'previewListClicked',
              props: {
                ruleType: 'simple',
                isV3sharesheet: true,
                sharesheetType: 'collection',
              },
            });
          },
          onCustomRuleSelect,
          selectedCustomRule,
          canInviteEmail: canInviteCurrentUser ?? false,
          handleOnEmailInvite,
          hasUserInvitePermission,
          isPinned,
          onIsPinnedChange,
        }
      : {
          view: 'previewMembers',
          options: previewFilterOptions,
          onPreviewModalBackButtonClick: () => setView('peopleWithAccess'),
          rules: displayRules,
          onLoadMore: fetchNextPageForPreview,
          hasNextPage: hasNextPageForPreview ?? false,
          onSearchOrFilterList,
          isLoadingPreview,
          isFetchingMoreMembers: isFetchingNextPageForPreview,
        };

  return (
    <>
      <ShareModal
        isOpen={Boolean(collectionId)}
        onClose={handleCloseWithConfirmationModal}
        onGetHelpClick={() => {
          trackSharesheetAction({
            actionName: 'getHelpClicked',
            props: {
              ruleType: 'simple',
              isV3sharesheet: true,
              sharesheetType: 'collection',
            },
          });
          window.open(
            'https://www.joinassembly.com/articles/collections',
            '_blank',
            'noopener,noreferrer'
          );
        }}
        onDoneAction={onDoneAction}
        isSaveInProgress={isSaveInProgress}
        {...props}
      />
      <ShareConfirmationModal
        description={formatMessage(messages.confirmationModalBody)}
        rightButton={
          <Button
            isFullWidth
            onClick={() => {
              setIsConfirmationModalOpen(false);
              onDoneAction();
            }}
            variation="primary"
          >
            {formatMessage(messages.saveChanges)}
          </Button>
        }
        onClose={() => {
          setIsConfirmationModalOpen(false);
          closeShareCollectionModal();
        }}
        open={isConfirmationModalOpen}
        leftButton={
          <Button
            isFullWidth
            onClick={() => {
              setIsConfirmationModalOpen(false);
              closeShareCollectionModal();
            }}
            variation="secondaryEmphasized"
          >
            {formatMessage(messages.discardChanges)}
          </Button>
        }
        title={formatMessage(messages.confirmationModalTitle)}
      />
    </>
  );
}
